import { deleteObject, getDownloadURL, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage';
import { storage } from '../firebaseConfig';

// Uploads go under {folder}/{profileId}/... (see storage.rules).

/**
 * Upload a file and resolve its download URL. `onProgress(bytesTransferred, totalBytes)`
 * is called as the upload advances.
 */
export function uploadFile(path, file, { onProgress, metadata } = {}) {
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(ref(storage, path), file, metadata);
    task.on(
      'state_changed',
      (snapshot) => onProgress && onProgress(snapshot.bytesTransferred, snapshot.totalBytes),
      reject,
      async () => {
        try {
          resolve(await getDownloadURL(task.snapshot.ref));
        } catch (error) {
          reject(error);
        }
      },
    );
  });
}

/** Simple upload without progress; resolves the download URL. */
export async function uploadFileSimple(path, file, metadata) {
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, file, metadata);
  return getDownloadURL(fileRef);
}

/**
 * Start a cancellable upload. Returns { task, done } where done resolves the download URL.
 * Used where the caller may need task.cancel() (e.g. background photo uploads).
 */
export function startUpload(path, file, metadata) {
  const task = uploadBytesResumable(ref(storage, path), file, metadata);
  const done = new Promise((resolve, reject) => {
    task.on('state_changed', null, reject, async () => {
      try {
        resolve(await getDownloadURL(task.snapshot.ref));
      } catch (error) {
        reject(error);
      }
    });
  });
  return { task, done };
}

/** Delete a file by storage path or download URL. */
export function deleteFile(pathOrUrl) {
  return deleteObject(ref(storage, pathOrUrl));
}

/** Storage path for a download URL (or path), or null if it isn't one. */
export function storagePathOf(pathOrUrl) {
  try {
    return ref(storage, pathOrUrl).fullPath;
  } catch {
    return null;
  }
}
